import React, { Fragment, useState, useRef, useEffect, useCallback } from 'react';
import { useGlobalContext } from 'Context/GlobalContext';
import { useCedulasContext } from 'Context/CedulasPageContext.js';
import { WEB as styles, PNG_IMP, PNG_IMP_L, PNG_SCALE, PNG_EXPORT_PX } from './styles.js';
import { COMPARTIR, REPORTAR, DESCARGAR, REDES_CONVIDA, REDES_SINVIDA } from 'Assets/index.js';
import { toBase64, urlToFile } from 'Utils/helpers';
import html2canvas from 'html2canvas';
import { pdf } from '@react-pdf/renderer';
import MissingPersonPng from './MissingPersonPng.jsx';
import MissingPersonPngImpresion from './MissingPersonPngImpresion.jsx';
import MissingPersonPdf from './MissingPersonPdf.js';
import { insertPhys } from 'Utils/pngPhys';
import { waitForPngCapture, rasterizeSvgImages } from './pngCapture';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { 
    Box, Button, CircularProgress, Menu, MenuItem,
    Typography, ListItemIcon, ListItemText
} from '@mui/material';
import { QRCode, message, Modal } from 'antd';

// react-pdf incrusta la foto tal cual y los visores no la suavizan al ampliar: se lleva aqui al
// tamaño de impresion (870 px = 300 ppi) con el suavizado del navegador, estirada al recuadro
// como en la impresion. El canvas ademas normaliza cualquier formato/mime a JPEG.
const toPrintPhoto = async (src, size = PNG_IMP.photoBox.width) => {
    const img = new Image();
    img.src = src;
    await img.decode();
    const canvas = Object.assign(document.createElement("canvas"), { width: size, height: size });
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(img, 0, 0, size, size);
    return canvas.toDataURL("image/jpeg", 0.92);
};

// Sello «LOCALIZADA» (1080×1080) de la descarga Redes: se pinta sobre la captura.
const SELLO_LOCALIZADA = { 'CON VIDA': REDES_CONVIDA, 'SIN VIDA': REDES_SINVIDA };

const MissingPersonCardFooter = ({ missingPersonData, onPressReport, onPressShare }) => {
    const { responsiveScreen, downloadFile } = useGlobalContext();
    const { cedulaAnalyticsEvent } = useCedulasContext();

    const { nombre_completo, id_cedula_busqueda, ruta_foto, sexo, condicion_localizacion } = missingPersonData;

    const [{
        isLoadingImage, isErrorImage, checkDownloadImage, isLoadingPng,
        isLoadingPdf, isErrorPdf, checkDownloadPdf, openModal,
        isLoadingImpresion, isErrorImpresion, checkDownloadImpresion, mountImpresion,
        pngRequestId, pngRequestPhoto
    }, setDownloadStatus] = useState({
        isLoadingImage: false, isErrorImage: false, checkDownloadImage: false, isLoadingPdf: false,
        isErrorPdf: false, checkDownloadPdf: false, openModal: false, isLoadingPng: false,
        isLoadingImpresion: false, isErrorImpresion: false, checkDownloadImpresion: false, mountImpresion: false,
        pngRequestId: null, pngRequestPhoto: null
    });
    const [ anchorEl, setAnchorEl ] = useState(null);

    const imageRef = useRef(null);
    const impresionRef = useRef(null);
    const qrRef = useRef(null);
    const open = Boolean(anchorEl);
    const formatName = nombre_completo.split(" ").join("_").toLowerCase();

    const handleDownloadStatus = (status) => setDownloadStatus((prev) => ({ ...prev, ...status }))
    const handleClick = (event) => { setAnchorEl(event.currentTarget) };
    const handleClose = () => { setAnchorEl(null) };

    const handleDownloadPNG = () => {
        handleDownloadStatus({ 
            isLoadingImage: true, isErrorImage: false,
            openModal: true,
            checkDownloadImage: false, isLoadingPng: false,
            pngRequestId: id_cedula_busqueda, pngRequestPhoto: ruta_foto
        });
    };

    const cancelDownloadPNG = () => handleDownloadStatus({
        isLoadingImage: false, isErrorImage: false, checkDownloadImage: false,
        isLoadingPng: false, openModal: false, pngRequestId: null, pngRequestPhoto: null,
    });

    useEffect(() => {
        setDownloadStatus((prev) => ({ ...prev, isLoadingImpresion: false, mountImpresion: false }));
    }, [id_cedula_busqueda, ruta_foto]);

    useEffect(() => {
        if (pngRequestId !== null && (pngRequestId !== id_cedula_busqueda || pngRequestPhoto !== ruta_foto)) {
            setDownloadStatus((prev) => ({
                ...prev,
                isLoadingImage: false,
                isLoadingPng: false,
                openModal: false,
                pngRequestId: null,
                pngRequestPhoto: null,
            }));
        }
    }, [id_cedula_busqueda, ruta_foto, pngRequestId, pngRequestPhoto]);

    useEffect(() => {
        let cancelled = false;

        const getCedula = async () => {
            try {
                const component = imageRef.current;
                if (!component) return;

                await waitForPngCapture(component);
                if (cancelled || pngRequestId !== id_cedula_busqueda || pngRequestPhoto !== ruta_foto) return;

                const canvas = await html2canvas(component, {
                    scale: PNG_SCALE,
                    backgroundColor: '#ffffff',
                    useCORS: true,
                    onclone: rasterizeSvgImages(PNG_SCALE),
                });
                if (cancelled || pngRequestId !== id_cedula_busqueda || pngRequestPhoto !== ruta_foto) return;

                if (canvas.width !== canvas.height) {
                    throw new Error(`La cédula capturada no es cuadrada: ${canvas.width}x${canvas.height}`);
                }

                let downloadCanvas = canvas;
                if (canvas.width !== PNG_EXPORT_PX) {
                    downloadCanvas = document.createElement('canvas');
                    downloadCanvas.width = PNG_EXPORT_PX;
                    downloadCanvas.height = PNG_EXPORT_PX;

                    const context = downloadCanvas.getContext('2d');
                    if (!context) {
                        throw new Error('No fue posible crear el lienzo de descarga.');
                    }

                    context.fillStyle = '#ffffff';
                    context.fillRect(0, 0, PNG_EXPORT_PX, PNG_EXPORT_PX);
                    context.drawImage(canvas, 0, 0, PNG_EXPORT_PX, PNG_EXPORT_PX);
                }

                const sello = SELLO_LOCALIZADA[condicion_localizacion];
                if (sello) {
                    const img = new Image();
                    img.src = sello;
                    await img.decode();
                    const context = downloadCanvas.getContext('2d');
                    // html2canvas deja su escala y desplazamiento (según el scroll) en el contexto.
                    context.setTransform(1, 0, 0, 1, 0, 0);
                    context.drawImage(img, 0, 0, PNG_EXPORT_PX, PNG_EXPORT_PX);
                }

                const imgURL = downloadCanvas.toDataURL('image/png', 1.0);
                if (cancelled || pngRequestId !== id_cedula_busqueda || pngRequestPhoto !== ruta_foto) return;

                downloadFile(imgURL, `cedula_${formatName || 'desconocido'}.png`);
                handleDownloadStatus({ isErrorImage: false, checkDownloadImage: true });
                message.success("Descarga de imagen con éxito.");
                cedulaAnalyticsEvent('Descarga imagen de cédula de búsqueda', 'Descargar en formato de imagen la cédula de persona desaparecida');
            } catch (error) {
                if (!cancelled && pngRequestId === id_cedula_busqueda && pngRequestPhoto === ruta_foto) {
                    handleDownloadStatus({ isErrorImage: true });
                    message.error("Error inesperado al generar la imagen.");
                    console.error(error);
                }
            } finally {
                if (!cancelled && pngRequestId === id_cedula_busqueda && pngRequestPhoto === ruta_foto) {
                    handleDownloadStatus({
                        isLoadingImage: false,
                        isLoadingPng: false,
                        openModal: false,
                        pngRequestId: null,
                        pngRequestPhoto: null,
                    });
                }
            }
        };

        if (isLoadingPng && openModal && pngRequestId === id_cedula_busqueda && pngRequestPhoto === ruta_foto) {
            getCedula();
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal, isLoadingPng, pngRequestId, pngRequestPhoto, id_cedula_busqueda, ruta_foto]);

    const handleDownloadImpresion = () => {
        handleDownloadStatus({
            isLoadingImpresion: true, isErrorImpresion: false,
            checkDownloadImpresion: false, mountImpresion: true
        });
    };

    const handleImpresionReady = async (diagnostico) => {
        const node = impresionRef.current;
        try {
            if (diagnostico) console.info('Diagnóstico cédula impresión:', diagnostico);
            if (!node) throw new Error('Nodo de impresión no disponible.');

            const canvas = await html2canvas(node, {
                scale: 1, backgroundColor: '#ffffff', useCORS: true, onclone: rasterizeSvgImages(1),
            });
            if (node !== impresionRef.current) return;

            if (canvas.width !== PNG_IMP_L.width || canvas.height !== PNG_IMP_L.height) {
                throw new Error(`La cédula de impresión no mide ${PNG_IMP_L.width}x${PNG_IMP_L.height}: ${canvas.width}x${canvas.height}`);
            }

            const blob = await new Promise((resolve, reject) =>
                canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob devolvió null.')), 'image/png', 1.0)
            );
            if (node !== impresionRef.current) return;
            // toBlob exporta a 96 dpi; se reescribe pHYs a 300 ppi (11811 px/m) sobre el PNG final.
            const withPhys = insertPhys(new Uint8Array(await blob.arrayBuffer()));
            if (node !== impresionRef.current) return;
            const url = window.URL.createObjectURL(new Blob([withPhys], { type: 'image/png' }));
            downloadFile(url, `cedula_${formatName || 'desconocido'}_impresion.png`);

            handleDownloadStatus({ isErrorImpresion: false, checkDownloadImpresion: true });
            message.success("Descarga de imagen para impresión con éxito.");
            cedulaAnalyticsEvent('Descarga imagen de cédula de búsqueda para impresión', 'Descargar en formato de imagen para impresión la cédula de persona desaparecida');
        } catch (error) {
            if (node === impresionRef.current) {
                handleDownloadStatus({ isErrorImpresion: true });
                message.error("Error inesperado al generar la imagen para impresión.");
                console.error(error);
            }
        } finally {
            if (node === impresionRef.current) {
                handleDownloadStatus({ isLoadingImpresion: false, mountImpresion: false });
            }
        }
    };

    const handleImpresionError = useCallback((error) => {
        setDownloadStatus((prev) => ({
            ...prev,
            isErrorImpresion: true,
            isLoadingImpresion: false,
            mountImpresion: false,
        }));
        message.error("Error inesperado al generar la imagen para impresión.");
        console.error(error);
    }, []);

    const handleDownloadPDF = async () => {
        handleDownloadStatus({ isLoadingPdf: true });

        try {
            const canvas = qrRef.current?.querySelector('canvas');

            if (canvas) {
                const urlQR = canvas.toDataURL("image/png", 1.0);
                const fallbackPhoto = `${window.location.origin}/no_image.png`;
                const photoFile = await urlToFile(missingPersonData.ruta_foto).catch(() => false);
                const photoSrc = photoFile
                    ? await toBase64(photoFile).then(toPrintPhoto).catch(() => fallbackPhoto)
                    : fallbackPhoto;

                const blob = await pdf(
                    <MissingPersonPdf 
                        missingPersonData={missingPersonData}
                        photoSrc={photoSrc}
                        qrCodeString={urlQR} 
                    />
                ).toBlob();
                const urlPdf = window.URL.createObjectURL(blob);
                downloadFile(urlPdf, `cedula_${formatName || 'desconocido'}.pdf`);
                handleDownloadStatus({ isErrorPdf: false, checkDownloadPdf: true });
                cedulaAnalyticsEvent('Descarga cédula de búsqueda en PDF', 'Descargar en formato PDF la cédula de persona desaparecida');
                message.success("Descarga de PDF con éxito.");
            }
        } catch (error) {
            handleDownloadStatus({ isErrorPdf: true });
            message.error("Error al generar el PDF, intentalo de nuevo.");
        } finally {
            handleDownloadStatus({ isLoadingPdf: false });
        }
    }

    return (
        <Fragment>
            <Box component="footer" sx={styles.actionContainer}>
                <Button onClick={() => onPressReport(nombre_completo, id_cedula_busqueda, sexo)}>
                    <Box sx={styles.actionItem}>
                        <img src={REPORTAR} style={styles.actionImg} alt={'Reportar icono'} />
                        {!responsiveScreen && <Typography sx={styles.actionText}>Reportar</Typography>}
                    </Box>
                </Button>
                <Button onClick={handleClick}>
                    <Box sx={styles.actionItem}>
                        <img src={DESCARGAR} style={styles.actionImg} alt={'Descargar icono'} ></img>
                        {!responsiveScreen && <Typography sx={styles.actionText}>Descargar</Typography>}
                    </Box>
                </Button>
                <Menu
                    id="menu-downloads"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ 'aria-labelledby': 'button-menu-downloads' }}
                >
                    <MenuItem onClick={handleDownloadPDF}>
                        <ListItemIcon sx={{ color: isErrorPdf ? "red" : checkDownloadPdf ? "green" : "secondary" }}>
                            {isLoadingPdf ? <CircularProgress size={24} /> : <PictureAsPdfOutlinedIcon/>}
                        </ListItemIcon>
                        <ListItemText>PDF · Impresión</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDownloadPNG} disabled={isLoadingImage}>
                        <ListItemIcon sx={{ color: isErrorImage ? "red" : checkDownloadImage ? "green" : "secondary" }}>
                            {isLoadingImage ? <CircularProgress size={24} /> : <ImageOutlinedIcon/>}
                        </ListItemIcon>
                        <ListItemText>Imagen · Redes</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDownloadImpresion} disabled={isLoadingImpresion}>
                        <ListItemIcon sx={{ color: isErrorImpresion ? "red" : checkDownloadImpresion ? "green" : "secondary" }}>
                            {isLoadingImpresion ? <CircularProgress size={24} /> : <ImageOutlinedIcon/>}
                        </ListItemIcon>
                        <ListItemText>Imagen · Pantallas</ListItemText>
                    </MenuItem>
                </Menu>
                <Button onClick={() => onPressShare(id_cedula_busqueda)}>
                    <Box sx={styles.actionItem}>
                        <img src={COMPARTIR} style={styles.actionImg} alt={'Compartir icono'} />
                        {!responsiveScreen && <Typography sx={styles.actionText}>Compartir</Typography>}
                    </Box>
                </Button>
            </Box>
            <div ref={qrRef} style={{ display: 'none' }}>
                <QRCode 
                    type="canvas" 
                    bordered={false} 
                    value={`${window.location.protocol}//${window.location.host}/cedulas-de-busqueda?id_cedula_busqueda=${id_cedula_busqueda}&qr=true`} 
                />
            </div>
            <Modal
                open={openModal}
                footer={null}
                destroyOnHidden
                centered
                closable={false}
                onCancel={cancelDownloadPNG}
                styles={{ content: {
                    boxShadow: 'none', border: 'none', width: 540,
                    borderColor: 'transparent', padding: 0, margin: 0
                }}}
            >
                <div ref={imageRef} style={{ width: 540, height: 540, overflow: 'hidden' }}>
                    {openModal && (
                        <MissingPersonPng
                            key={`${id_cedula_busqueda}-${ruta_foto}`}
                            missingPersonData={missingPersonData}
                            handleDownloadStatus={handleDownloadStatus}
                        />
                    )}
                </div>
            </Modal>
            {mountImpresion && (
                <div style={{ position: 'fixed', left: '-99999px', top: 0 }}>
                    <MissingPersonPngImpresion
                        key={`${id_cedula_busqueda}-${ruta_foto}`}
                        ref={impresionRef}
                        missingPersonData={missingPersonData}
                        onReady={handleImpresionReady}
                        onError={handleImpresionError}
                    />
                </div>
            )}
        </Fragment>
    )
}

export default MissingPersonCardFooter;
